import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, Loader2, Check, X, ArrowLeft, Users } from 'lucide-react';
import axios from 'axios';
import Sparkles from '../components/Sparkles';
import syncService from '../services/SyncService';
import autoSync from '../services/AutoSync';
import tokenStore from '../services/tokenStore';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useSecureAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null); // { business_name, invited_by_name, role }

  // If user arrived via a magic-link invite, validate it and pre-fill
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(
          `${API_URL}/api/team/validate-invite/${encodeURIComponent(inviteToken)}`
        );
        if (cancelled) return;
        if (resp.data?.valid) {
          setEmail(resp.data.email || '');
          if (resp.data.name) setName(resp.data.name);
          setInvitePreview({
            business_name: resp.data.business_name || 'your team',
            invited_by_name: resp.data.invited_by_name,
            role: resp.data.role,
            workspace_id: resp.data.workspace_id,
          });
        } else {
          toast.error(resp.data?.error || 'This invitation link is invalid or expired');
        }
      } catch (e) {
        console.warn('[Register] Invite validation failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken]);

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    // Owners (not joining via invite) must name their business — this is what
    // provisions their team workspace on the backend.
    if (!invitePreview && !businessName.trim()) {
      toast.error('Please enter your business name');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await register(email, password, name, invitePreview ? undefined : businessName);
      
      if (result.success) {
        // === A) Auto-join team via magic-link token (preferred) OR pre-approved email (fallback) ===
        let joinedTeam = false;
        let assignedRole = null;
        try {
          if (inviteToken && invitePreview) {
            // Magic-link path — use the invite directly
            syncService.setWorkspaceId(invitePreview.workspace_id);
            assignedRole = invitePreview.role;
            // Mark token consumed (best-effort) — passes user_id so backend
            // also adds the user to workspace_members for sync authorization.
            const newUserId = result.user?.id;
            const authToken = tokenStore.getAccessToken();
            const authCfg = authToken
              ? { headers: { Authorization: `Bearer ${authToken}` }, withCredentials: true }
              : { withCredentials: true };
            axios.post(
              `${API_URL}/api/team/consume-invite/${encodeURIComponent(inviteToken)}` +
              (newUserId ? `?user_id=${encodeURIComponent(newUserId)}` : ''),
              {},
              authCfg
            ).catch(() => {});
            joinedTeam = true;
          } else {
            // No token — try email-based pre-approval as fallback
            const authToken = tokenStore.getAccessToken();
            const resp = await axios.get(
              `${API_URL}/api/team/check-invite/${encodeURIComponent(email)}`,
              authToken
                ? { headers: { Authorization: `Bearer ${authToken}` }, withCredentials: true }
                : { withCredentials: true }
            );
            if (resp.data?.found && resp.data?.workspace_id) {
              syncService.setWorkspaceId(resp.data.workspace_id);
              assignedRole = resp.data.role || 'employee';
              joinedTeam = true;
              // Register membership for sync authorization
              const newUserId = result.user?.id;
              if (newUserId) {
                const authToken = tokenStore.getAccessToken();
                axios.post(
                  `${API_URL}/api/team/register-member`,
                  {
                    workspace_id: resp.data.workspace_id,
                    user_id: newUserId,
                    email,
                    role: assignedRole,
                    joined_via: 'email_auto',
                  },
                  authToken
                    ? { headers: { Authorization: `Bearer ${authToken}` } }
                    : undefined
                ).catch(() => {});
              }
            } else {
              // Not an invitee — this is a brand-new OWNER creating a business.
              // Provision their team workspace now so multi-device + team sync
              // works immediately (and future invited employees share it).
              const authToken = tokenStore.getAccessToken();
              const ewResp = await axios.post(
                `${API_URL}/api/team/ensure-workspace`,
                {},
                authToken
                  ? { headers: { Authorization: `Bearer ${authToken}` } }
                  : undefined
              );
              if (ewResp.data?.workspace_id) {
                syncService.setWorkspaceId(ewResp.data.workspace_id);
                joinedTeam = true;
              }
            }
          }
          if (joinedTeam && assignedRole) {
            const stored = JSON.parse(localStorage.getItem('maidly_current_user') || '{}');
            localStorage.setItem(
              'maidly_current_user',
              JSON.stringify({ ...stored, role: assignedRole })
            );
          }
        } catch (e) {
          console.warn('[Register] team auto-join failed:', e?.message || e);
        }

        // === Mirror new user into Employee Management list ===
        try {
          const existing = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
          const alreadyThere = existing.some((e) => e?.email === email);
          if (!alreadyThere) {
            const newEmployee = {
              id: result.user?.id || Date.now().toString(),
              name,
              email,
              phone: '',
              role: result.user?.role || 'employee',
              hourlyRate: 0,
              username: email,
              status: 'active',
              photo: '',
              createdAt: new Date().toISOString(),
            };
            localStorage.setItem(
              'maidly_employees',
              JSON.stringify([...existing, newEmployee])
            );
          }
        } catch (e) {
          console.warn('[Register] Failed to mirror user into employees list:', e);
        }

        // If we joined a team, pull the team's shared data NOW so the dashboard
        // already shows it on first render.
        if (joinedTeam) {
          try {
            await autoSync.pullFromCloud();
          } catch (e) {
            console.warn('[Register] pullFromCloud failed:', e);
          }
          toast.success("Welcome! Your workspace is ready — data syncs across your devices and staff.");
        } else {
          toast.success('Account created successfully!');
        }
        // Hard navigation so AuthContext (which gates ProtectedRoute) re-mounts
        // and hydrates the newly-registered user from 'maidly_current_user'.
        // A plain SPA navigate would keep AuthContext.user=null -> /login.
        window.location.assign('/dashboard');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ passed, label }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      fontSize: '0.8rem',
      color: passed ? '#16a34a' : '#9ca3af'
    }}>
      {passed ? <Check size={14} /> : <X size={14} />}
      {label}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Grok-style floating starry background */}
      <Sparkles count={45} color="rgba(255,255,255,0.85)" minSize={6} maxSize={18} />

      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        position: 'relative',
        zIndex: 1
      }}>
        <button
          type="button"
          data-testid="back-to-home-btn"
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.875rem',
            padding: '0.25rem 0.5rem'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#0073ea'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
        >
          <ArrowLeft size={16} />
          Home
        </button>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
            Create Account
          </h1>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.25rem' }}>
            Join Maidly Business Manager
          </p>
        </div>

        {/* Invite banner (visible only when arrived via a magic-link) */}
        {invitePreview && (
          <div
            data-testid="invite-banner"
            style={{
              background: 'linear-gradient(135deg, #eaf3ff 0%, #dbe9ff 100%)',
              border: '1px solid #0073ea',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              marginBottom: '1.5rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
            }}
          >
            <Users size={20} color="#0073ea" style={{ flexShrink: 0, marginTop: '0.15rem' }} />
            <div style={{ fontSize: '0.85rem', color: '#0b2545', lineHeight: 1.5 }}>
              <strong>{invitePreview.invited_by_name || 'Your team'}</strong> invited you to join{' '}
              <strong>{invitePreview.business_name}</strong> as a <strong>{invitePreview.role}</strong>.
              Complete sign-up below to instantly access shared customers, schedules, and team data.
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Full Name
            </label>
            <div style={{ position: 'relative' }}>
              <User size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                required
                data-testid="register-name"
                style={{
                  width: '100%',
                  padding: '0.875rem 0.875rem 0.875rem 2.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Business name — only for owners creating a business (hidden when
              joining an existing team via invite). Drives workspace creation
              so team + multi-device sync works out of the box. */}
          {!invitePreview && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Business Name
              </label>
              <div style={{ position: 'relative' }}>
                <Users size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g. Sparkle Cleaning Co."
                  required
                  data-testid="register-business-name"
                  style={{
                    width: '100%',
                    padding: '0.875rem 0.875rem 0.875rem 2.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.4rem 0 0' }}>
                Creates your team workspace so your data syncs across devices and staff.
              </p>
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Email
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="register-email"
                style={{
                  width: '100%',
                  padding: '0.875rem 0.875rem 0.875rem 2.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                data-testid="register-password"
                style={{
                  width: '100%',
                  padding: '0.875rem 2.75rem 0.875rem 2.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {showPassword ? <EyeOff size={18} color="#9ca3af" /> : <Eye size={18} color="#9ca3af" />}
              </button>
            </div>
            
            {/* Password Requirements */}
            {password.length > 0 && (
              <div style={{ 
                marginTop: '0.75rem', 
                padding: '0.75rem', 
                background: '#f9fafb', 
                borderRadius: '8px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem'
              }}>
                <PasswordCheck passed={passwordChecks.length} label="8+ characters" />
                <PasswordCheck passed={passwordChecks.uppercase} label="Uppercase letter" />
                <PasswordCheck passed={passwordChecks.number} label="Number" />
                <PasswordCheck passed={passwordChecks.special} label="Special character" />
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Confirm Password
            </label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                data-testid="register-confirm-password"
                style={{
                  width: '100%',
                  padding: '0.875rem 2.75rem 0.875rem 2.75rem',
                  border: `1px solid ${confirmPassword.length > 0 ? (passwordsMatch ? '#16a34a' : '#ef4444') : '#e5e7eb'}`,
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {confirmPassword.length > 0 && (
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                  {passwordsMatch ? <Check size={18} color="#16a34a" /> : <X size={18} color="#ef4444" />}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !isPasswordValid || !passwordsMatch}
            data-testid="register-submit-btn"
            style={{
              width: '100%',
              padding: '1rem',
              background: isPasswordValid && passwordsMatch ? '#0073ea' : '#e5e7eb',
              color: isPasswordValid && passwordsMatch ? '#fff' : '#9ca3af',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: isPasswordValid && passwordsMatch && !loading ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : null}
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        {/* Login Link */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Already have an account?{' '}
            <Link
              to="/secure-login"
              data-testid="login-link"
              style={{
                color: '#0073ea',
                fontWeight: 600,
                textDecoration: 'none'
              }}
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
